import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Timer,
  FolderKanban,
  Users,
  BarChart3,
  Settings,
  LogOut,
  ChevronRight,
  Wallet,
  Crown,
  Clock,
  Calculator,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePaymentSummary } from "@/hooks/usePayments";
import { useProfile } from "@/hooks/useProfile";
import { usePlan } from "@/hooks/usePlan";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { TrialIndicator } from "@/components/trial/TrialIndicator";
import logoFreelaDash from "@/assets/logo-freeladash.png";

const mainMenuItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Timer", url: "/timer", icon: Timer },
  { title: "Projetos", url: "/projects", icon: FolderKanban },
  { title: "Clientes", url: "/clients", icon: Users },
  { title: "Pagamentos", url: "/payments", icon: Wallet, hasBadge: true },
  { title: "Calculadora", url: "/calculator", icon: Calculator },
];

const secondaryMenuItems = [
  { title: "Relatórios", url: "/reports", icon: BarChart3 },
  { title: "Configurações", url: "/settings", icon: Settings },
];

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { data: paymentSummary } = usePaymentSummary();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { user } = useAuth();
  const { isPro, isTrial, isTrialExpired, trialDaysUsed, isLoading: planLoading } = usePlan();
  
  const overdueCount = paymentSummary?.count_overdue || 0;

  // Get user initials for avatar
  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    }
    return user?.email?.slice(0, 2).toUpperCase() || "U";
  };

  // Get display name
  const displayName = profile?.full_name || user?.email?.split("@")[0] || "Usuário";

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("Logout realizado com sucesso!");
      navigate("/login");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      toast.error("Erro ao fazer logout");
    }
  };

  const renderMenuItem = (item: typeof mainMenuItems[0]) => {
    const isActive = location.pathname.startsWith(item.url);
    const showBadge = item.hasBadge && overdueCount > 0;
    
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={item.title}
          className={cn(
            "group relative h-11 rounded-xl transition-all duration-200 ease-out",
            "hover:bg-muted",
            isActive && [
              "bg-primary text-primary-foreground font-medium",
              "hover:bg-primary/90",
            ]
          )}
        >
          <Link to={item.url} className="flex items-center gap-3 px-3">
            <div className="relative flex-shrink-0">
              <item.icon className={cn(
                "h-[18px] w-[18px] transition-colors duration-200",
                isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground"
              )} />
              {showBadge && collapsed && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-destructive" />
              )}
            </div>
            <span className={cn(
              "font-medium transition-colors duration-200",
              isActive ? "text-primary-foreground" : "text-foreground/80 group-hover:text-foreground"
            )}>
              {item.title}
            </span>
            {showBadge && !collapsed && (
              <Badge variant="destructive" className="ml-auto h-5 px-1.5 text-[10px]">
                {overdueCount}
              </Badge>
            )}
            {isActive && !collapsed && !showBadge && (
              <ChevronRight className="ml-auto h-4 w-4 text-primary-foreground/70" />
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0 bg-muted/30">
      {/* Logo Header */}
      <SidebarHeader className="p-5 pb-8">
        <Link 
          to="/dashboard" 
          className="flex items-center gap-3 group"
        >
          <img 
            src={logoFreelaDash} 
            alt="FreelaDash" 
            className={cn(
              "h-10 w-10 rounded-xl object-contain",
              "transition-transform duration-200 group-hover:scale-105"
            )}
          />
          {!collapsed && (
            <span className="font-display text-2xl font-bold tracking-wide text-foreground" style={{ WebkitTextStroke: '0.5px currentColor' }}>
              FREELADASH
            </span>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-3">
        {/* Main Navigation */}
        <SidebarGroup>
          {!collapsed && (
            <SidebarGroupLabel className="px-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-3">
              Menu Principal
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-1.5">
              {mainMenuItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Secondary Navigation */}
        <SidebarGroup className="mt-8">
          {!collapsed && (
            <SidebarGroupLabel className="px-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-3">
              Gerenciamento
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-1.5">
              {secondaryMenuItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Trial Indicator */}
        {!collapsed && !isPro && (
          <SidebarGroup className="mt-6 px-2">
            <TrialIndicator variant="sidebar" />
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* User Footer */}
      <SidebarFooter className="p-4 pt-6">
        <Link
          to="/settings"
          className={cn(
            "flex items-center gap-3 rounded-xl p-3",
            "bg-card/60 hover:bg-card",
            "transition-all duration-200 ease-out",
            "group cursor-pointer"
          )}
        >
          <div className="relative flex-shrink-0">
            <Avatar className="h-9 w-9 ring-2 ring-background/80 shadow-sm">
              {profile?.avatar_url && (
                <AvatarImage src={profile.avatar_url} alt={displayName} />
              )}
              <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                {profileLoading ? "..." : getInitials()}
              </AvatarFallback>
            </Avatar>
            {/* Online indicator */}
            <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-card" />
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 overflow-hidden">
                {profileLoading ? (
                  <>
                    <Skeleton className="h-4 w-24 mb-1" />
                    <Skeleton className="h-3 w-16" />
                  </>
                ) : (
                  <>
                    <p className="truncate text-sm font-medium text-foreground">
                      {displayName}
                    </p>
                    <div className="flex items-center gap-1.5">
                      {planLoading ? (
                        <Skeleton className="h-3 w-16" />
                      ) : isPro ? (
                        <>
                          <Crown className="h-3 w-3 text-primary" />
                          <span className="text-xs font-medium text-primary">Pro</span>
                        </>
                      ) : isTrialExpired ? (
                        <>
                          <Clock className="h-3 w-3 text-destructive" />
                          <span className="text-xs font-medium text-destructive">Trial expirado</span>
                        </>
                      ) : isTrial ? (
                        <>
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Trial ({trialDaysUsed}/7)</span>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">Trial</span>
                      )}
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLogout();
                }}
                className={cn(
                  "p-2 rounded-lg",
                  "text-muted-foreground hover:text-primary",
                  "hover:bg-primary/10",
                  "transition-all duration-200 ease-out"
                )}
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          )}
        </Link>
      </SidebarFooter>
    </Sidebar>
  );
}
